import React, { useEffect, useMemo, useState } from 'react';
import { Check, Maximize2 } from 'lucide-react';
import type { Task, TaskAssignment, TaskProjectLink } from '../../../../shared/entities';
import { TaskService } from '../../../services/taskService';
import { loadTasksBundle } from '../../tasks/taskDataLoader';
import {
  visibleMemberIds,
  isTaskVisible,
  taskBelongsToProject,
  canEditTasks,
  canCreateTask,
} from '../../tasks/taskPermissions';
import { Icon } from '../../../components/ui/Icon';
import { WidgetFrame, WidgetActionButton, WList, type WListRow } from '../../../components/widgets';
import {
  COLOR_DANGER,
  COLOR_INFO,
  COLOR_OK,
  countChip,
  AddRow,
  todayIso,
  useSettledFetch,
} from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

// ═══════════════════════════════════════════════════════════════
// TASKS (T3B merge of my_tasks + risks) — this project's tasks the
// viewer may see (tasks.view_own/_team/_all), sorted attention-first
// (overdue, then priority, then due date). Every risks action survives:
// + New task (header), complete toggle (canEditTasks-gated, as
// my_tasks had it), per-task click-through, deep-link to the Tasks page.
// ═══════════════════════════════════════════════════════════════

const PRIORITY_ORDER: Record<Task['priority'], number> = { urgent: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_COLOR: Record<Task['priority'], string> = {
  urgent: COLOR_DANGER,
  high: 'var(--color-warning)',
  medium: 'var(--color-info)',
  low: 'var(--color-text-subtle)',
};

function TasksBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [links, setLinks] = useState<TaskProjectLink[]>([]);
  const [serverScoped, setServerScoped] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const userId = ctx.authUser?.teamMemberId || '';
  const canEdit = canEditTasks(ctx.userPermissions);

  // Decision 24: ONE shared loader for tasks + assignments + project
  // links. `assignmentsScoped` (assignments 403'd) replaces this widget's
  // former own "trust the server" special case — same meaning, now
  // decided once in taskDataLoader for every consumer.
  const { status, reload } = useSettledFetch(
    () => [loadTasksBundle()],
    (results) => {
      const [bR] = results;
      if (bR.status === 'fulfilled') {
        setTasks(bR.value.tasks);
        setServerScoped(bR.value.assignmentsScoped);
        setAssignments(bR.value.assignments);
        setLinks(bR.value.links);
      }
    },
    [ctx.project.id]
  );

  useEffect(() => {
    const onSync = () => reload();
    window.addEventListener('specforma-sync-complete', onSync);
    return () => window.removeEventListener('specforma-sync-complete', onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const visIds = visibleMemberIds({
      userId,
      permissions: ctx.userPermissions,
      members: ctx.members,
      teams: ctx.teams,
      teamAssignments: ctx.teamAssignments,
    });
    return tasks.filter(
      (t) =>
        taskBelongsToProject(t, links, ctx.project.id) &&
        (serverScoped || isTaskVisible(t, assignments, userId, visIds))
    );
  }, [
    serverScoped,
    tasks,
    assignments,
    links,
    userId,
    ctx.userPermissions,
    ctx.members,
    ctx.teams,
    ctx.teamAssignments,
    ctx.project.id,
  ]);

  const today = todayIso();
  const doneCount = useMemo(() => visible.filter((t) => t.status === 'done').length, [visible]);
  const open = useMemo(() => visible.filter((t) => t.status !== 'done'), [visible]);
  const overdueCount = useMemo(
    () => open.filter((t) => !!t.dueDate && t.dueDate < today).length,
    [open, today]
  );

  // Attention-first: overdue, then priority, then due date.
  const sorted = useMemo(
    () =>
      [...open].sort((a, b) => {
        const aOver = !!a.dueDate && a.dueDate < today ? 0 : 1;
        const bOver = !!b.dueDate && b.dueDate < today ? 0 : 1;
        if (aOver !== bOver) return aOver - bOver;
        const pr = (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
        if (pr !== 0) return pr;
        return (a.dueDate || '9999-12-31').localeCompare(b.dueDate || '9999-12-31');
      }),
    [open, today]
  );

  const openTasks = () => ctx.navigate?.({ view: 'tasks', projectId: ctx.project.id });

  async function toggleComplete(t: Task) {
    if (savingIds.has(t.id)) return;
    const prevStatus = t.status;
    const newStatus: Task['status'] = t.status === 'done' ? 'todo' : 'done';
    const updated = { ...t, status: newStatus };
    setSavingIds((prev) => new Set(prev).add(t.id));
    setTasks((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
    try {
      await TaskService.saveTask(updated);
    } catch (err: any) {
      setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: prevStatus } : x)));
      ctx.toast(err?.message || 'Failed to update task', true);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
    }
  }

  const isEmpty = visible.length === 0;
  // Stays 'ready' (never 'empty') even with zero tasks: the "+ New task"
  // row lives in the WList footer now (moved from the header button,
  // Decision 2/3), and WidgetFrame's 'empty' status would replace the
  // whole body with just an EmptyState — dropping the always-available
  // add path the header button used to guarantee. The "No tasks" message
  // + "Open tasks" link render inline in the body instead, above the
  // (still-present) add row.
  const frameStatus = status === 'loading' ? 'loading' : status === 'error' ? 'error' : 'ready';

  const rows: WListRow[] = sorted.map((t) => {
    const isOverdue = !!t.dueDate && t.dueDate < today;
    return {
      id: t.id,
      lead: (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 'var(--space-2)',
            height: 'var(--space-2)',
            borderRadius: 'var(--radius-full)',
            background: PRIORITY_COLOR[t.priority],
          }}
        />
      ),
      primary: t.title,
      meta: (
        <span
          style={{ color: isOverdue ? COLOR_DANGER : undefined, fontWeight: isOverdue ? 700 : 400 }}
        >
          {t.dueDate || '—'}
        </span>
      ),
      onClick: () => ctx.onDrill({ kind: 'task', taskId: t.id }),
      trailing: canEdit ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void toggleComplete(t);
          }}
          disabled={savingIds.has(t.id)}
          aria-label={`Mark ${t.title} complete`}
          aria-pressed={t.status === 'done'}
          style={{
            width: 'var(--space-5)',
            height: 'var(--space-5)',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--bd)',
            background: 'transparent',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: savingIds.has(t.id) ? 'default' : 'pointer',
            opacity: savingIds.has(t.id) ? 0.5 : 1,
            flexShrink: 0,
            color: 'var(--tm)',
          }}
        >
          <Icon icon={Check} size="xs" />
        </button>
      ) : undefined,
    };
  });

  const canCreate = canCreateTask(ctx.userPermissions) && !ctx.locked;

  return (
    <WidgetFrame
      title="Tasks"
      status={frameStatus}
      form="list"
      error={{ message: 'Could not load tasks.', onRetry: reload }}
      actions={<WidgetActionButton icon={Maximize2} label="Open tasks" onClick={openTasks} />}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {isEmpty ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--tm)',
              padding: 'var(--space-1) var(--space-2)',
            }}
          >
            <span>No tasks for this project.</span>
            <button
              type="button"
              onClick={openTasks}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: 'var(--ac)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Open tasks
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <span style={countChip(COLOR_INFO)}>{open.length} open</span>
            {overdueCount > 0 && (
              <span style={countChip(COLOR_DANGER)}>{overdueCount} overdue</span>
            )}
            {doneCount > 0 && <span style={countChip(COLOR_OK)}>{doneCount} done</span>}
          </div>
        )}
        <WList
          rows={rows}
          max={8}
          onViewAll={openTasks}
          footer={
            canCreate ? (
              <AddRow label="New task" onClick={() => ctx.onDrill({ kind: 'new-task' })} />
            ) : undefined
          }
        />
      </div>
    </WidgetFrame>
  );
}

export const tasksWidget: ProjectWidgetDef = {
  id: 'tasks',
  title: 'Tasks',
  description:
    'This project’s tasks the viewer may see, attention-first (overdue, then priority, then due). Absorbs Risks / Open Items.',
  category: 'overview',
  requiresAny: ['tasks.view_own', 'tasks.view_team', 'tasks.view_all'],
  cols: { min: 3, max: 12 },
  sizes: ['compact', 'standard', 'expanded'],
  defaultSize: 'standard',
  inputSurface: false,
  render: (ctx) => <TasksBody ctx={ctx} />,
};
